import React from 'react';
import { Box, Card, CardActionArea, Chip, Grid, Paper, Stack, Typography, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StarIcon from '@mui/icons-material/Star';
import HistoryIcon from '@mui/icons-material/History';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tool, toolCategories, tools } from '../config/tools';
import { pagePath } from '../seo/seoData';
import { toolAccent } from '../theme';
import { useFavorites, useRecents } from '../hooks/useToolPrefs';

interface HomePageProps {
  lang: string;
}

const HERO_GRADIENTS = {
  dark: 'linear-gradient(90deg, #90caf9 0%, #b39ddb 50%, #f48fb1 100%)',
  light: 'linear-gradient(90deg, #1565c0 0%, #6a1b9a 50%, #c2185b 100%)',
};

const ToolCard: React.FC<{ tool: Tool; lang: string }> = ({ tool, lang }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const Icon = tool.icon;
  const accent = toolAccent(tool.color, theme.palette.mode);

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'border-color 0.2s, transform 0.2s',
        '&:hover': { borderColor: accent, transform: 'translateY(-3px)' },
        '&:hover .tool-arrow': { transform: 'translateX(4px)', color: accent },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={pagePath(lang, tool.slug)}
        sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 40,
              height: 40,
              borderRadius: 2.5,
              flexShrink: 0,
              backgroundColor: `${tool.color}1f`,
              color: accent,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {t(`tools.${tool.id}.title`)}
          </Typography>
        </Box>
        <Typography color="text.secondary" variant="body2" sx={{ flexGrow: 1 }}>
          {t(`tools.${tool.id}.description`)}
        </Typography>
        <ArrowForwardIcon
          className="tool-arrow"
          fontSize="small"
          sx={{ mt: 1.5, color: 'text.secondary', transition: 'transform 0.2s, color 0.2s' }}
        />
      </CardActionArea>
    </Card>
  );
};

// Compact chips for the user's own shortcuts (favorites / recently used).
const ShortcutRow: React.FC<{ title: string; icon: React.ReactNode; ids: string[]; lang: string }> = ({
  title,
  icon,
  ids,
  lang,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const items = ids.map((id) => tools.find((tool) => tool.id === id)).filter((tool): tool is Tool => !!tool);
  if (!items.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon}
        {title}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {items.map((tool) => {
          const Icon = tool.icon;
          return (
            <Chip
              key={tool.id}
              component={RouterLink}
              to={pagePath(lang, tool.slug)}
              clickable
              icon={<Icon style={{ color: toolAccent(tool.color, theme.palette.mode) }} />}
              label={t(`tools.${tool.id}.shortTitle`)}
              variant="outlined"
            />
          );
        })}
      </Stack>
    </Box>
  );
};

const HomePage: React.FC<HomePageProps> = ({ lang }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { favorites } = useFavorites();
  const recents = useRecents();
  const badges = t('home.badges', { returnObjects: true }) as string[];

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
      <Box component="header" sx={{ textAlign: 'center', pt: { xs: 3, md: 6 }, pb: { xs: 4, md: 5 } }}>
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.03em',
            fontSize: { xs: 34, sm: 44, md: 56 },
            background: HERO_GRADIENTS[theme.palette.mode],
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t('home.title')}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ mt: 2, mx: 'auto', maxWidth: 680, fontSize: { xs: 16, md: 19 }, lineHeight: 1.6 }}
        >
          {t('home.subtitle')}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
          {badges.map((badge) => (
            <Chip key={badge} icon={<CheckCircleOutlineIcon />} label={badge} variant="outlined" color="primary" />
          ))}
        </Stack>
      </Box>

      <ShortcutRow
        title={t('home.favorites')}
        icon={<StarIcon fontSize="small" sx={{ color: 'warning.main' }} />}
        ids={favorites}
        lang={lang}
      />
      <ShortcutRow
        title={t('home.recent')}
        icon={<HistoryIcon fontSize="small" color="action" />}
        ids={recents}
        lang={lang}
      />

      {toolCategories.map((category) => (
        <Box component="section" key={category.id} sx={{ mt: 4 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
            {t(`categories.${category.id}`)}
          </Typography>
          <Grid container spacing={2}>
            {category.tools.map((tool) => (
              <Grid key={tool.id} item xs={12} sm={6} md={4}>
                <ToolCard tool={tool} lang={lang} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      <Paper
        component="section"
        sx={{ mt: 5, p: { xs: 3, md: 4 }, display: 'flex', gap: 2, alignItems: 'flex-start' }}
      >
        <LockOutlinedIcon color="success" sx={{ mt: 0.5 }} />
        <Box>
          <Typography variant="h6" component="h2" fontWeight={700}>
            {t('home.privacyTitle')}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {t('home.privacyText')}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default HomePage;
